import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { isoDateTime, paisa } from '@fleet/shared';
import { useTodayQuery } from '@/api/endpoints';
import type { BoardBooking } from '@/api/types';
import { LinkButton } from '@/design/Button';
import { Identifier, MoneyText } from '@/design/Data';
import { DataTable } from '@/design/DataTable';
import type { Column } from '@/design/DataTable';
import { EmptyState, ErrorState } from '@/design/EmptyState';
import { Icon } from '@/design/icons';
import { Panel } from '@/design/Panel';
import { StatusPill } from '@/design/StatusPill';
import { bookingStatus } from '@/features/counter/status';
import { useTime } from '@/lib/time';

/**
 * Today.
 *
 * The screen the counter opens in the morning and leaves open all day. Order is
 * deliberate: what is missing first, then what moves today. Because nothing in
 * this business is automatic, the system's job is to surface what has not been
 * entered — not to invent it.
 */
export function TodayPage(): ReactElement {
  const { data, isLoading, isError, refetch } = useTodayQuery(undefined, {
    // A counter screen left open all day goes stale silently otherwise.
    pollingInterval: 60_000,
  });
  const { formatTime, formatDay } = useTime();

  const movement = (which: 'out' | 'back'): readonly Column<BoardBooking>[] => [
    {
      key: 'time',
      header: which === 'out' ? 'Out at' : 'Back at',
      // The date under the time, because QA read a bare "09:30" and could not
      // tell whether it meant today. Everything in these two panels *is*
      // today, but a screen that requires you to know that is a screen that
      // gets double-checked, and double-checking is what costs the minute.
      render: (row) => {
        const when = isoDateTime(which === 'out' ? row.start_at : row.end_at);
        return (
          <div className="flex flex-col">
            <span className="tnum font-medium">{formatTime(when)}</span>
            <span className="text-micro text-ink-muted">{formatDay(when)}</span>
          </div>
        );
      },
    },
    {
      key: 'reference',
      header: 'Booking',
      render: (row) => <Identifier>{row.reference}</Identifier>,
    },
    { key: 'customer', header: 'Customer', render: (row) => row.customer_name },
    {
      key: 'phone',
      header: 'Phone',
      secondary: true,
      render: (row) => (
        <a href={`tel:${row.customer_phone}`} className="identifier hover:underline">
          {row.customer_phone}
        </a>
      ),
    },
    {
      key: 'vehicle',
      header: 'Vehicle',
      render: (row) =>
        row.vehicle_name === null || row.vehicle_name === undefined ? (
          <span className="text-attention">Not assigned</span>
        ) : (
          row.vehicle_name
        ),
    },
    {
      key: 'total',
      header: 'Total',
      numeric: true,
      secondary: true,
      render: (row) => <MoneyText amount={paisa(row.total ?? 0)} />,
    },
  ];

  /**
   * The day in five numbers, in the order the panels below come in.
   *
   * They used to run the other way round, so the eye went left-to-right along
   * the top and then jumped back up the page to find the panel. QA asked for
   * the two to match; the panel order is the deliberate one — what is missing
   * first — so the numbers moved.
   *
   * "Overdue back", not "Overdue": QA could not tell whether it meant late
   * returning the vehicle or late paying. It is the vehicle, always.
   */
  const figures = [
    {
      label: 'Needs attention',
      value: data?.attention.length ?? 0,
      emphasis: 'attention' as const,
    },
    { label: 'Going out', value: data?.going_out.length ?? 0, emphasis: 'neutral' as const },
    { label: 'Coming back', value: data?.coming_back.length ?? 0, emphasis: 'neutral' as const },
    { label: 'On trip', value: data?.on_trip.length ?? 0, emphasis: 'neutral' as const },
    { label: 'Overdue back', value: data?.overdue.length ?? 0, emphasis: 'alert' as const },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-display font-semibold">Today</h1>
        <p className="text-ink-muted">{formatDay(isoDateTime(new Date().toISOString()))}</p>
      </div>

      {isError && (
        <ErrorState
          headline="Today's board did not load"
          detail="The server did not answer. Everything already entered is safe."
          onRetry={() => void refetch()}
          retryLabel="Load again"
        />
      )}

      {/* The day in five numbers. Tabular, so the row does not shift as they change. */}
      <div className="grid grid-cols-2 divide-rule border border-rule bg-panel sm:grid-cols-3 lg:grid-cols-5 lg:divide-x">
        {figures.map((figure) => (
          <div key={figure.label} className="flex flex-col gap-0.5 px-4 py-3">
            <span className="text-micro font-medium uppercase text-ink-muted">{figure.label}</span>
            <span
              className={[
                'tnum text-figure font-semibold',
                figure.value === 0
                  ? 'text-ink'
                  : figure.emphasis === 'alert'
                    ? 'text-alert'
                    : figure.emphasis === 'attention'
                      ? 'text-attention'
                      : 'text-ink',
              ].join(' ')}
            >
              {figure.value}
            </span>
          </div>
        ))}
      </div>

      <Panel
        title="Needs attention"
        note="Bookings with something missing. This is what keeps the month's figure honest."
        current
      >
        {data === undefined || data.attention.length === 0 ? (
          <EmptyState
            headline="Nothing is missing"
            detail="A booking appears here when it comes back with no payment recorded, goes out with no handover, passes its return time without being checked in, or closes with no invoice."
          />
        ) : (
          <ul className="divide-y divide-rule">
            {data.attention.map((item) => (
              <li key={`${item.kind}-${item.booking_id}`} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-2.5">
                <Link to={`/bookings/${item.booking_id}`} className="identifier font-medium hover:underline">
                  {item.reference}
                </Link>
                <span className="font-medium">{item.customer}</span>
                <span className="text-attention">{item.what}</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel
          title="Going out"
          note="Handover due today"
          actions={
            <LinkButton to="/calendar" intent="quiet">
              <Icon name="calendar" width="16" height="16" />
              Open calendar
            </LinkButton>
          }
        >
          <DataTable
            columns={movement('out')}
            rows={data?.going_out ?? []}
            rowKey={(row) => row.id}
            rowHref={(row) => `/bookings/${row.id}`}
            isLoading={isLoading}
            isError={false}
            errorHeadline=""
            errorDetail=""
            empty={{
              headline: 'No vehicles going out today',
              detail:
                'Confirmed bookings starting today are listed here, in departure order, with the vehicle and the driver.',
              action: (
                <LinkButton to="/bookings/new" intent="primary">
                  <Icon name="plus" width="16" height="16" />
                  New booking
                </LinkButton>
              ),
            }}
          />
        </Panel>

        <Panel
          title="Coming back"
          note={
            (data?.came_back.length ?? 0) === 0
              ? 'Return due today'
              : `Return due today · ${String(data?.came_back.length ?? 0)} already back`
          }
        >
          <DataTable
            columns={movement('back')}
            rows={data?.coming_back ?? []}
            rowKey={(row) => row.id}
            rowHref={(row) => `/bookings/${row.id}`}
            isLoading={isLoading}
            isError={false}
            errorHeadline=""
            errorDetail=""
            empty={{
              headline: 'No vehicles due back today',
              detail:
                'Bookings ending today are listed here so the counter knows which keys to expect and when.',
            }}
          />
        </Panel>
      </div>

      {/*
        What has already come back.

        QA asked whether the checked-in ones belonged on this board. They do,
        and for a reason the other panels miss: "Coming back" empties as the
        day goes on, so by five o'clock a board with eight returns behind it
        and two to come looks like a quiet day. Shown only when there is
        something in it — an empty panel every morning is furniture.
      */}
      {(data?.came_back.length ?? 0) > 0 && (
        <Panel title="Back already" note="Checked in today. Nothing left to do on these.">
          <DataTable
            columns={movement('back')}
            rows={data?.came_back ?? []}
            rowKey={(row) => row.id}
            rowHref={(row) => `/bookings/${row.id}`}
            isLoading={false}
            isError={false}
            errorHeadline=""
            errorDetail=""
            empty={{ headline: '', detail: '' }}
          />
        </Panel>
      )}

      <Panel
        title="Out on the road"
        note="Every vehicle currently with a customer. Overdue means past its return time — not unpaid."
        actions={
          data !== undefined && data.overdue.length > 0 ? (
            <span className="text-small font-medium text-alert">
              {data.overdue.length} past their return time
            </span>
          ) : undefined
        }
      >
        <DataTable
          columns={[
            {
              key: 'reference',
              header: 'Booking',
              render: (row: BoardBooking) => <Identifier>{row.reference}</Identifier>,
            },
            { key: 'customer', header: 'Customer', render: (row: BoardBooking) => row.customer_name },
            {
              key: 'vehicle',
              header: 'Vehicle',
              render: (row: BoardBooking) => row.vehicle_name ?? '—',
            },
            {
              key: 'due',
              header: 'Due back',
              render: (row: BoardBooking) => {
                const due = isoDateTime(row.end_at);
                const late = new Date(row.end_at).getTime() < Date.now();
                return (
                  <span className={late ? 'font-medium text-alert' : ''}>
                    {formatDay(due)} {formatTime(due)}
                    {late ? ' — overdue' : ''}
                  </span>
                );
              },
            },
            {
              key: 'status',
              header: 'Status',
              render: (row: BoardBooking) => (
                <StatusPill status={bookingStatus(row.status)} />
              ),
            },
          ]}
          rows={data?.on_trip ?? []}
          rowKey={(row) => row.id}
          rowHref={(row) => `/bookings/${row.id}`}
          isLoading={isLoading}
          isError={false}
          errorHeadline=""
          errorDetail=""
          empty={{
            headline: 'Nothing is out',
            detail: 'Vehicles appear here once a booking is marked as gone out.',
          }}
        />
      </Panel>

      <Panel
        title="Enquiries waiting"
        note="Calls and messages with no answer yet"
        actions={
          <LinkButton to="/enquiries" intent="quiet">
            Open enquiries
          </LinkButton>
        }
      >
        {data !== undefined && data.open_enquiries > 0 ? (
          <div className="px-4 py-6 text-center">
            <p className="tnum text-figure font-semibold">{data.open_enquiries}</p>
            <p className="mt-1 text-ink-muted">
              waiting for an answer. Every one is somebody who wanted to give you money.
            </p>
            <div className="mt-4 flex justify-center">
              <LinkButton to="/enquiries" intent="primary">
                Answer them
              </LinkButton>
            </div>
          </div>
        ) : (
          <EmptyState
            headline="No enquiries waiting"
            detail="Add the call you just took and it stays on this list until someone quotes it or marks it lost."
            action={
              <LinkButton to="/enquiries" intent="primary">
                <Icon name="plus" width="16" height="16" />
                Add enquiry
              </LinkButton>
            }
          />
        )}
      </Panel>

    </div>
  );
}
